import { Edit, Visibility } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { ClusterShipperDto } from 'dtos/clusterShipperDto'
import { ClusterShipperThirdPartyConversionDeliveryDto } from 'dtos/clusterShipperThirdPartyConversionDeliveryDto'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import ClusterShippersThirdPartyConversionDeliveryAddEditDialog from './ClusterShippersThirdPartyConversionDeliveryAddEditDialog'
import ClusterShippersThirdPartyConversionDeliveryViewDialog from './ClusterShippersThirdPartyConversionDeliveryViewDialog'
import { useDialogState, useLoadingState } from 'hooks'
import ClusterShippersThirdPartyConversionDeliveryImageCard from './ClusterShippersThirdPartyConversionDeliveryImageCard'

interface ClusterShippersThirdPartyConversionDeliveryPhotoCardProps {
  clusterShipper: ClusterShipperDto
}

export default function ClusterShippersThirdPartyConversionDeliveryPhotoCard({
  clusterShipper
}: ClusterShippersThirdPartyConversionDeliveryPhotoCardProps) {
  //const { permissons } = useAuth()

  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingClusterShippersThirdPartyConversionDelivery: false,
    isGettingClusterShippersThirdPartyConversionDelivery: false,
    isDeletingClusterShippersThirdPartyConversionDeliveryPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    clusterShippersThirdPartyConversionDeliveryAddEditDialogOpen: false,
    clusterShippersThirdPartyConversionDeliveryViewDialogOpen: false
  })

  const [thirdPartyConversionDeliveryPhoto, setThirdPartyConversionDeliveryPhoto] =
    useState<ClusterShipperThirdPartyConversionDeliveryDto>(new ClusterShipperThirdPartyConversionDeliveryDto())

  useEffect(() => {
    if (clusterShipper.clusterShipperGuid) {
      setLoadingState('isGettingClusterShippersThirdPartyConversionDelivery', true)
      controllers
        .getClusterShippersThirdPartyConversionDeliveryInformation(clusterShipper.clusterShipperGuid)
        .then(response => {
          setThirdPartyConversionDeliveryPhoto(response.value)
        })
        .finally(() => {
          setLoadingState('isGettingClusterShippersThirdPartyConversionDelivery', false)
        })
    }
  }, [clusterShipper])

  return (
    <>
      <ClusterShippersThirdPartyConversionDeliveryAddEditDialog
        initialValues={thirdPartyConversionDeliveryPhoto}
        isLoading={loadingState.isUpdatingClusterShippersThirdPartyConversionDelivery}
        onClose={() => {
          setDialogState('clusterShippersThirdPartyConversionDeliveryAddEditDialogOpen', false)
        }}
        onSave={values => {
          setLoadingState('isUpdatingClusterShippersThirdPartyConversionDelivery', true)
          controllers
            .updateClusterShippersThirdPartyConversionDeliveryInformation(clusterShipper.clusterShipperGuid!, values)
            .then(response => {
              enqueueSnackbar('3rd Party Conversion Delivery Photo Saved Successfully!', { variant: 'success' })
              setThirdPartyConversionDeliveryPhoto(response.value)
              setDialogState('clusterShippersThirdPartyConversionDeliveryAddEditDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingClusterShippersThirdPartyConversionDelivery', false)
            })
        }}
        open={dialogState.clusterShippersThirdPartyConversionDeliveryAddEditDialogOpen}
        clusterShipperGuid={clusterShipper.clusterShipperGuid!}
      />

      <ClusterShippersThirdPartyConversionDeliveryViewDialog
        initialValues={thirdPartyConversionDeliveryPhoto}
        clusterShipperGuid={clusterShipper.clusterShipperGuid!}
        onClose={() => {
          setDialogState('clusterShippersThirdPartyConversionDeliveryViewDialogOpen', false)
        }}
        open={dialogState.clusterShippersThirdPartyConversionDeliveryViewDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  3rd Party Conversion Delivery Photo
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setDialogState('clusterShippersThirdPartyConversionDeliveryViewDialogOpen', true)
                  }}
                  disabled={!Boolean(thirdPartyConversionDeliveryPhoto.clusterShipperThirdPartyConversionDeliveryPhotoFileName)}
                  variant='text'
                  endIcon={<Visibility />}
                >
                  VIEW SELECTED
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {loadingState.isGettingClusterShippersThirdPartyConversionDelivery && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            <Grid item xs={12} container alignItems='flex-start' mt={0.125}>
              <Grid item xs={12} md={4}>
                <ClusterShippersThirdPartyConversionDeliveryImageCard
                  complete={Boolean(thirdPartyConversionDeliveryPhoto.clusterShipperThirdPartyConversionDeliveryPhotoFileName)}
                  fileName={thirdPartyConversionDeliveryPhoto.clusterShipperThirdPartyConversionDeliveryPhotoFileName}
                  clusterShipperGuid={clusterShipper.clusterShipperGuid}
                />
              </Grid>
            </Grid>

            {/* {permissions.CLUSTERS_ADD_EDIT && (

            )} */}
            <Grid item xs={12} sm='auto'>
              <Button
                onClick={() => {
                  setDialogState('clusterShippersThirdPartyConversionDeliveryAddEditDialogOpen', true)
                }}
                variant='text'
                endIcon={<Edit />}
              >
                UPLOAD/REPLACE SELECTED
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <GreenText variant='outlined'>
              {`**VIEW SELECTED Button:**

The “VIEW SELECTED” button is only available if the user has selected photos to view.

Clicking the “VIEW SELECTED” button will open the View Check In Photo Dialog.

**3rd Party Conversion Delivery:**

Displays the 3rd Party Conversion Delivery image uploaded from the Cluster Shipper Details.

If no image has been uploaded, the “No Image” icon will be displayed instead.

Clicking the 3rd Party Conversion Delivery image will select it.

**Upload/Replace Selected:**

Clicking this button will open up “Upload 3rd Party Conversion Delivery Photo Dialog”

This button is only displayed if a photo has been selected`}
            </GreenText>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
